import _ from 'lodash';

import { Application } from '@breathelife/types';
import { StaticSubscriber, VersionedAnswers } from '@zinnia/form-engine-sdk';

import { TFunction } from 'i18next';
import { isAnswerTimestampMismatchError } from '../../../Helpers/assistedApplication/answers';
import { notificationSlice } from '../../../ReduxStore/Notification/NotificationSlice';
import { Dispatch } from '../../../ReduxStore/types';
import { getApplication } from '../../../Services/ApplicationsService';
import { updateAssistedApplicationAnswers } from '../../../Services/AssistedApplicationsService';

type ApplicationId = string;
type SaveState = {
  answersToBeSentToBackend: VersionedAnswers;
  lastAnswersSentToBackend: VersionedAnswers | undefined;
  lastAnswersUpdatedAt: Date | null;
  isAwaitingOngoingBackendCall: boolean;
  hasEncounteredError: boolean;
  debouncedNetworkCall: _.DebouncedFunc<typeof callTheNetwork>;
};

const DEBOUNCE_DELAY = 2000;

const getDefaultModuleState = (
  lastAnswersUpdatedAt: Date | null,
  answersToBeSentToBackend: VersionedAnswers,
  lastAnswersSentToBackend: VersionedAnswers,
): SaveState => ({
  lastAnswersUpdatedAt,
  answersToBeSentToBackend, // Set by the lambda after initial load.
  lastAnswersSentToBackend,
  isAwaitingOngoingBackendCall: false,
  hasEncounteredError: false,
  debouncedNetworkCall: _.debounce(callTheNetwork, DEBOUNCE_DELAY),
});

const applicationState = new Map<ApplicationId, SaveState>();
let currentApplication: string = '';

export function unsetCurrentApplication(): void {
  currentApplication = '';
}

export const sendAnswersToBackend = (
  application: Application,
  openConfirmationModal: () => void,
  setIsAnswersSubscriberActive: (value: boolean) => void,
  dispatch: Dispatch,
  t: TFunction,
): StaticSubscriber => {
  // #REGION RUN-ONCE-WHEN-SUBSCRIBER-LOADS
  currentApplication = application.id;

  // Only initialize state if it doesn't exist for this application.
  // This prevents resetting the lastAnswersUpdatedAt timestamp when the
  // questionnaireEngine is recreated (e.g., due to areAllMedicalInterviewCompleted changing).
  if (!applicationState.has(application.id)) {
    applicationState.set(
      application.id,
      getDefaultModuleState(
        application.answersUpdatedAt,
        new VersionedAnswers({ v1: _.cloneDeep(application.answers), v2: _.cloneDeep(application.answersV2) }),
        new VersionedAnswers({ v1: _.cloneDeep(application.answers), v2: _.cloneDeep(application.answersV2) }),
      ),
    );
  }

  // #END-REGION RUN-ONCE-WHEN-SUBSCRIBER-LOADS

  return async (questionnaireEngine) => {
    setIsAnswersSubscriberActive(true);

    const appState = applicationState.get(application.id);
    if (!appState) {
      // eslint-disable-next-line no-console
      console.warn('saveAnswersSubscriber should always have a state for that application. ');
      return;
    }

    if (application.submitted) {
      setIsAnswersSubscriberActive(false);
      return;
    }

    const frozenAnswers = _.cloneDeep(questionnaireEngine.getAnswerResolverInstance().dump());

    appState.answersToBeSentToBackend = frozenAnswers;

    if (appState.isAwaitingOngoingBackendCall || frozenAnswers.isEqual(appState.lastAnswersSentToBackend)) {
      setIsAnswersSubscriberActive(false);
      return;
    }

    void appState.debouncedNetworkCall(
      application.id,
      frozenAnswers,
      openConfirmationModal,
      setIsAnswersSubscriberActive,
      dispatch,
      t,
    );
  };
};

async function callTheNetwork(
  applicationId: ApplicationId,
  answersToSend: VersionedAnswers,
  openConfirmationModal: () => void,
  setIsAnswersSubscriberActive: (value: boolean) => void,
  dispatch: Dispatch,
  t: TFunction,
): Promise<void> {
  const appState = applicationState.get(applicationId);

  if (!appState) {
    return;
  }

  appState.isAwaitingOngoingBackendCall = true;

  try {
    const { answersUpdatedAt } = await updateAssistedApplicationAnswers(applicationId, {
      answers: answersToSend.v1,
      answersV2: answersToSend.v2,
      answersUpdatedAt: appState.lastAnswersUpdatedAt,
    });
    appState.lastAnswersSentToBackend = answersToSend;
    appState.lastAnswersUpdatedAt = new Date(answersUpdatedAt);
  } catch (error: unknown) {
    appState.hasEncounteredError = true;

    if (isAnswerTimestampMismatchError(error)) {
      try {
        const latestApplication = await getApplication(applicationId);

        const backendAnswersMatchAttemptedSave =
          _.isEqual(latestApplication.answers, answersToSend.v1) &&
          _.isEqual(latestApplication.answersV2, answersToSend.v2);

        if (backendAnswersMatchAttemptedSave) {
          appState.lastAnswersSentToBackend = answersToSend;
          appState.lastAnswersUpdatedAt = latestApplication.answersUpdatedAt
            ? new Date(latestApplication.answersUpdatedAt)
            : appState.lastAnswersUpdatedAt;
          appState.hasEncounteredError = false;
          return;
        }
      } catch (err: unknown) {
        openConfirmationModal();
        return;
      }

      if (applicationId === currentApplication) {
        openConfirmationModal();
        return;
      }
    }

    dispatch(
      notificationSlice.actions.setError({
        message: t('notifications.saveAnswersError'),
        autoHideDuration: 3000,
      }),
    );
  } finally {
    appState.isAwaitingOngoingBackendCall = false;

    if (
      !appState.answersToBeSentToBackend.isEqual(appState.lastAnswersSentToBackend) &&
      !appState.hasEncounteredError
    ) {
      void appState.debouncedNetworkCall(
        applicationId,
        appState.answersToBeSentToBackend,
        openConfirmationModal,
        setIsAnswersSubscriberActive,
        dispatch,
        t,
      );
    } else if (applicationId !== currentApplication) {
      void appState.debouncedNetworkCall.flush();
      applicationState.delete(applicationId);
    }

    if (applicationId === currentApplication) {
      setIsAnswersSubscriberActive(false);
    }
  }
}
